import { z } from "zod";

/**
 * SEO-3's Marketing dashboard contract — AI Search Visibility (how often
 * The Fox & The Owl surfaces when people ask Claude/ChatGPT/Perplexity/
 * Gemini for a Bulverde venue) and Google Rankings (Search
 * Console keyword positions). Built in parallel with SEO-2's api work per
 * the plan's contracts-first split, so every schema here is tolerant:
 * `.passthrough()` on objects, optional/defaulted fields, no enum pins on
 * free-form strings (`intent`) — the goal is "render defensively," not
 * "reject on drift" (validateResponse already logs-and-passes-through on
 * mismatch rather than throwing, per its own house rule).
 *
 * Rates/counts are server-authoritative per the house rule: this file only
 * parses and types them, it never derives a rate from raw counts.
 */

// ---- AI Search Visibility -----------------------------------------------------

export const AiProviderSchema = z.enum(["anthropic", "openai", "perplexity", "gemini"]);
export type AiProvider = z.infer<typeof AiProviderSchema>;

export const AI_PROVIDERS: AiProvider[] = ["anthropic", "openai", "perplexity", "gemini"];

export const AI_PROVIDER_LABELS: Record<AiProvider, string> = {
  anthropic: "Claude",
  openai: "ChatGPT",
  perplexity: "Perplexity",
  gemini: "Gemini",
};

export const ConfiguredProvidersSchema = z
  .object({
    anthropic: z.boolean().default(false),
    openai: z.boolean().default(false),
    perplexity: z.boolean().default(false),
    gemini: z.boolean().default(false),
  })
  .passthrough();
export type ConfiguredProviders = z.infer<typeof ConfiguredProvidersSchema>;

export const ProviderStatsSchema = z
  .object({
    checks: z.number().default(0),
    mentioned: z.number().default(0),
    recommended: z.number().default(0),
    cited: z.number().default(0),
  })
  .passthrough();
export type ProviderStats = z.infer<typeof ProviderStatsSchema>;

/**
 * Keyed by provider name (not a fixed z.enum key set) — the plan's contract
 * only guarantees `{provider: {...}}`, and a provider FoxCRM doesn't know
 * about yet shouldn't crash the page.
 */
export const ByProviderSchema = z.record(z.string(), ProviderStatsSchema);
export type ByProviderMap = z.infer<typeof ByProviderSchema>;

export const ByQueryRowSchema = z
  .object({
    query: z.string(),
    mentioned: z.boolean().default(false),
    recommended: z.boolean().default(false),
    cited: z.boolean().default(false),
  })
  .passthrough();
export type ByQueryRow = z.infer<typeof ByQueryRowSchema>;

export const AiSeoSnapshotSchema = z
  .object({
    week: z.string(),
    total_checks: z.number().default(0),
    mentioned: z.number().default(0),
    recommended: z.number().default(0),
    cited: z.number().default(0),
    mention_rate: z.number().default(0),
    recommend_rate: z.number().default(0),
    cite_rate: z.number().default(0),
    by_provider: ByProviderSchema.default({}),
    by_query: z.array(ByQueryRowSchema).default([]),
  })
  .passthrough();
export type AiSeoSnapshot = z.infer<typeof AiSeoSnapshotSchema>;

export const AiSeoTrendPointSchema = z
  .object({
    week: z.string(),
    mention_rate: z.number().default(0),
    recommend_rate: z.number().default(0),
    cite_rate: z.number().default(0),
  })
  .passthrough();
export type AiSeoTrendPoint = z.infer<typeof AiSeoTrendPointSchema>;

export const AiSeoOverviewSchema = z
  .object({
    configured_providers: ConfiguredProvidersSchema.default({
      anthropic: false,
      openai: false,
      perplexity: false,
      gemini: false,
    }),
    latest: AiSeoSnapshotSchema.nullable().default(null),
    trend: z.array(AiSeoTrendPointSchema).default([]),
    note: z.string().optional(),
  })
  .passthrough();
export type AiSeoOverview = z.infer<typeof AiSeoOverviewSchema>;

export const AiSeoSnapshotListResponseSchema = z
  .object({
    items: z.array(AiSeoSnapshotSchema).default([]),
  })
  .passthrough();
export type AiSeoSnapshotListResponse = z.infer<typeof AiSeoSnapshotListResponseSchema>;

// ---- Google Rankings (Search Console) ------------------------------------------

/**
 * `intent` is free-form on the wire per the contract (no enum pinned in the
 * plan) — kept as a plain nullable/optional string rather than a z.enum so
 * an unexpected value never trips schema validation.
 */
export const SeoKeywordRowSchema = z
  .object({
    phrase: z.string(),
    intent: z.string().nullable().optional(),
    position: z.number().nullable().optional(),
    clicks: z.number().nullable().optional(),
    impressions: z.number().nullable().optional(),
    ctr: z.number().nullable().optional(),
    captured_on: z.string().nullable().optional(),
  })
  .passthrough();
export type SeoKeywordRow = z.infer<typeof SeoKeywordRowSchema>;

export const SeoOverviewSchema = z
  .object({
    connected: z.boolean().default(false),
    note: z.string().optional(),
    keywords: z.array(SeoKeywordRowSchema).default([]),
  })
  .passthrough();
export type SeoOverview = z.infer<typeof SeoOverviewSchema>;

export const KeywordListItemSchema = z
  .object({
    id: z.string(),
    phrase: z.string(),
    intent: z.string().nullable().optional(),
    active: z.boolean().default(true),
  })
  .passthrough();
export type KeywordListItem = z.infer<typeof KeywordListItemSchema>;

export const KeywordListResponseSchema = z
  .object({
    items: z.array(KeywordListItemSchema).default([]),
  })
  .passthrough();
export type KeywordListResponse = z.infer<typeof KeywordListResponseSchema>;

export interface CreateKeywordInput {
  phrase: string;
  intent?: string;
}

export const GscStatusSchema = z
  .object({
    connected: z.boolean().default(false),
  })
  .passthrough();
export type GscStatus = z.infer<typeof GscStatusSchema>;

export const GscConnectResponseSchema = z
  .object({
    auth_url: z.string(),
  })
  .passthrough();
export type GscConnectResponse = z.infer<typeof GscConnectResponseSchema>;
